import { Epic } from 'redux-observable'
import { exhaustMap, filter, map, mergeMap, pluck } from 'rxjs/operators'
import { tag } from 'rxjs-spy/operators/tag'
import { isOfType } from 'typesafe-actions'
import { EpicDependencies, RootState } from 'src/state/types'
import { ENDPOINTS } from './constants'
import { events, TpnEvent } from './state'
import { TTpnSendResult, TTpnSettings } from './types'

type TpnEpic = Epic<TpnEvent, TpnEvent, RootState, EpicDependencies>

// 204 (not connected) arrives with a null response: that is the "not
// connected" signal, not an error.
export const apiFetchTpnSettings: TpnEpic = (action$, state$, { get, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.tpnSettings_request.type)),
    mergeMap(action =>
      get(ENDPOINTS.SETTINGS, state$, {}).pipe(
        pluck('response'),
        map((res: TTpnSettings | null | undefined) => events.tpnSettings_success(res ?? null)),
        catchRestError(action),
      ),
    ),
    tag('tpn/epics/apiFetchTpnSettings'),
  )

export const apiSaveTpnSettings: TpnEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.saveTpnSettings_request.type)),
    exhaustMap(action =>
      post(ENDPOINTS.SETTINGS, state$, action.payload, {}).pipe(
        map(() => events.saveTpnSettings_success()),
        catchRestError(action),
      ),
    ),
    tag('tpn/epics/apiSaveTpnSettings'),
  )

export const apiRemoveTpnSettings: TpnEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.removeTpnSettings_request.type)),
    exhaustMap(action =>
      post(ENDPOINTS.SETTINGS_REMOVE, state$, {}, {}).pipe(
        map(() => events.removeTpnSettings_success()),
        catchRestError(action),
      ),
    ),
    tag('tpn/epics/apiRemoveTpnSettings'),
  )

export const apiSetTpnAutoSend: TpnEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.setTpnAutoSend_request.type)),
    exhaustMap(action =>
      post(ENDPOINTS.SETTINGS_AUTO_SEND, state$, action.payload, {}).pipe(
        map(() => events.setTpnAutoSend_success(action.payload)),
        catchRestError(action),
      ),
    ),
    tag('tpn/epics/apiSetTpnAutoSend'),
  )

export const apiSendTpnReport: TpnEpic = (action$, state$, { post, catchRestError }) =>
  action$.pipe(
    filter(isOfType(events.sendTpnReport_request.type)),
    exhaustMap(action =>
      post(ENDPOINTS.SEND, state$, {}, {}).pipe(
        pluck('response'),
        map((res: TTpnSendResult) => events.sendTpnReport_success(res)),
        catchRestError(action),
      ),
    ),
    tag('tpn/epics/apiSendTpnReport'),
  )

// Re-read after every write. Save resolves the account number server side and
// send records a new lastSend, so the response to the write never carries the
// full picture.
export const refetchTpnSettings: TpnEpic = action$ =>
  action$.pipe(
    filter(
      isOfType([
        events.saveTpnSettings_success.type,
        events.removeTpnSettings_success.type,
        events.setTpnAutoSend_success.type,
        events.sendTpnReport_success.type,
      ]),
    ),
    map(() => events.tpnSettings_request()),
    tag('tpn/epics/refetchTpnSettings'),
  )
